import React, {
  cloneElement,
  useState,
  useCallback,
  useEffect,
  useRef,
  memo,
  MouseEventHandler
} from 'react';
import Popover from 'react-tiny-popover';
import { DateTime } from 'luxon';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { DateRange as BaseDateRange } from 'react-date-range';
import { ru, enGB, pl } from 'date-fns/locale';
import cn from 'classnames';
import { ReactComponent as CloseIcon } from 'images/icons/times.svg';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { getParam } from 'utils/url';
import { Lang } from 'utils/i18n';

import { useUpdateEffect } from 'react-use';
import { useLingui } from '@lingui/react/macro';
import styles from './DateRange.module.scss';

type Props = {
  children: React.ReactElement;
  onChange(date: { from: DateTime; to: DateTime }): void;
  from: DateTime;
  to: DateTime;
  className?: string;
};

const locales: {
  [key in Lang]: typeof ru;
} = {
  en: enGB,
  ru,
  pl
};

export const DateRange = memo(
  ({ children, onChange, className, from, to }: Props) => {
    const wasShown = useRef(false);
    const prevFrom = useRef(from);
    const prevTo = useRef(to);
    const [range, setRange] = useState({
      startDate: from.toJSDate(),
      endDate: to.toJSDate()
    });
    const [shown, setShown] = useState(false);

    const show = useCallback((e?: Event) => {
      e?.stopPropagation();
      setShown(true);
    }, []);
    const hide = useCallback((e?: MouseEvent) => {
      e?.stopPropagation();
      setShown(false);
    }, []);
    const { i18n } = useLingui();

    const handleBackdropClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        if ((e.target as HTMLDivElement).dataset.backdrop) {
          hide();
        }
      },
      [hide]
    );

    useUpdateEffect(() => {
      if (shown) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }, [shown]);

    useEffect(() => {
      if (shown) {
        wasShown.current = true;
      }
      if (!shown && wasShown.current) {
        const nextFrom = DateTime.fromJSDate(range.startDate).startOf('day');
        const nextTo = DateTime.fromJSDate(range.endDate).endOf('day');
        if (
          nextFrom.hasSame(prevFrom.current, 'day') &&
          nextTo.hasSame(prevTo.current, 'day')
        ) {
          return;
        }
        prevFrom.current = nextFrom;
        prevTo.current = nextTo;
        onChange({
          from: nextFrom,
          to: nextTo
        });
        wasShown.current = false;
      }
    }, [onChange, range.endDate, range.startDate, shown]);

    const updateRange = useCallback(
      (e: Record<string, { startDate: Date; endDate: Date }>) => {
        const [nextRange] = Object.keys(e);
        setRange(e[nextRange]);
      },
      []
    );

    return (
      <Popover
        isOpen={shown}
        containerClassName={cn(
          className,
          styles.popover,
          'react-tiny-popover-container'
        )}
        position="bottom"
        content={
          shown ? (
            <div
              className={styles.backdrop}
              onClick={handleBackdropClick}
              role="none"
              data-backdrop
            >
              <div className={styles.container}>
                <BaseDateRange
                  ranges={[range]}
                  onChange={updateRange}
                  locale={locales[i18n.locale as Lang]}
                />
                {/*
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                @ts-ignore */}
                <CloseIcon className={styles.close} onClick={hide} />
              </div>
            </div>
          ) : (
            <></>
          )
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onClickOutside={hide}
      >
        {cloneElement(children, { onClick: shown ? hide : show })}
      </Popover>
    );
  }
);

export function useDateRange(
  daysFrom = 1,
  defaults: { from?: DateTime; to?: DateTime } = {}
) {
  const now = DateTime.local();
  const [from, setFrom] = useState(() => {
    if (defaults.from) return defaults.from;
    const fromParam = getParam('from');
    if (fromParam) {
      return DateTime.fromISO(fromParam);
    }
    return now.startOf('day').minus({ days: daysFrom });
  });
  const [to, setTo] = useState(() => {
    if (defaults.to) return defaults.to;
    const toParam = getParam('to');
    if (toParam) {
      return DateTime.fromISO(toParam);
    }
    return now.endOf('day');
  });

  const setRange = useCallback((dates: { from: DateTime; to: DateTime }) => {
    setFrom(dates.from);
    setTo(dates.to);
  }, []);

  return [
    { from, to },
    { setFrom, setTo, setRange }
  ] as const;
}
